import React, { useState, useEffect } from 'react';
import { Button, Card, Input, Spinner } from '../design-system';
import { X, Copy, Check, Mail as MailIcon, Phone, MapPin, Building2, Edit, Save } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { apiPost } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const PracticeInfoModal = ({ isOpen, onClose, practice, isAdmin, onPracticeUpdated }) => {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: ''
    }
  });

  useEffect(() => {
    if (practice && isOpen) {
      setFormData({
        name: practice.name || '',
        description: practice.description || '',
        email: practice.email || '',
        phone: practice.phone || '',
        address: {
          street: practice.address?.street || '',
          city: practice.address?.city || '',
          state: practice.address?.state || '',
          zipCode: practice.address?.zipCode || ''
        }
      });
      setIsEditing(false);
    }
  }, [practice, isOpen]);

  if (!isOpen || !practice) return null;

  const copyPracticeId = () => {
    if (practice?.practiceId) {
      navigator.clipboard.writeText(practice.practiceId);
      setCopied(true);
      toast.success('Practice ID copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Practice name is required');
      return;
    }

    setIsSubmitting(true);

    try {
      const updateData = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        address: formData.address.street || formData.address.city || formData.address.state || formData.address.zipCode
          ? {
              street: formData.address.street.trim() || null,
              city: formData.address.city.trim() || null,
              state: formData.address.state.trim() || null,
              zipCode: formData.address.zipCode.trim() || null
            }
          : null
      };

      await apiPost('/.netlify/functions/update-practice', updateData, user);
      toast.success('Practice details updated successfully');
      setIsEditing(false);
      
      if (onPracticeUpdated) {
        onPracticeUpdated();
      }
    } catch (error) {
      console.error('Error updating practice:', error);
      toast.error(error.message || 'Failed to update practice details');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original practice data
    if (practice) {
      setFormData({
        name: practice.name || '',
        description: practice.description || '',
        email: practice.email || '',
        phone: practice.phone || '',
        address: {
          street: practice.address?.street || '',
          city: practice.address?.city || '',
          state: practice.address?.state || '',
          zipCode: practice.address?.zipCode || ''
        }
      });
    }
    setIsEditing(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card variant="elevated" padding="large" className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Practice Settings</h2>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && !isEditing && (
              <Button
                variant="ghost"
                size="small"
                onClick={() => setIsEditing(true)}
                title="Edit practice details"
              >
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </Button>
            )}
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="space-y-6">
          {/* Practice ID Section */}
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 block">
              Practice ID
            </label>
            <div className="p-4 bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg border border-primary/20">
              <div className="flex items-center justify-between gap-3">
                <code className="text-lg font-mono font-bold text-gray-900 flex-1">
                  {practice.practiceId}
                </code>
                <Button
                  variant="ghost"
                  size="small"
                  onClick={copyPracticeId}
                  className="flex items-center gap-2"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4 text-success" />
                      <span className="text-sm text-success font-medium">Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      <span className="text-sm">Copy</span>
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Share this ID with therapists who want to join your practice
              </p>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3 block">
              Contact Information
            </label>
            <div className="space-y-3">
              {isEditing ? (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      <MailIcon className="h-3 w-3 inline mr-1" />
                      Email
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="contact@practice.com"
                      disabled={isSubmitting}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      <Phone className="h-3 w-3 inline mr-1" />
                      Phone
                    </label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1-555-0123"
                      disabled={isSubmitting}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      <MapPin className="h-3 w-3 inline mr-1" />
                      Address
                    </label>
                    <div className="space-y-2 mt-1">
                      <Input
                        type="text"
                        name="address.street"
                        value={formData.address.street}
                        onChange={handleChange}
                        placeholder="Street Address"
                        disabled={isSubmitting}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          type="text"
                          name="address.city"
                          value={formData.address.city}
                          onChange={handleChange}
                          placeholder="City"
                          disabled={isSubmitting}
                        />
                        <Input
                          type="text"
                          name="address.state"
                          value={formData.address.state}
                          onChange={handleChange}
                          placeholder="State"
                          disabled={isSubmitting}
                        />
                      </div>
                      <Input
                        type="text"
                        name="address.zipCode"
                        value={formData.address.zipCode}
                        onChange={handleChange}
                        placeholder="ZIP Code"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {(practice.email || practice.phone || practice.address) ? (
                    <>
                      {practice.email && (
                        <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                          <MailIcon className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Email</p>
                            <p className="text-gray-900 mt-1 break-all">{practice.email}</p>
                          </div>
                        </div>
                      )}
                      {practice.phone && (
                        <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                          <Phone className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Phone</p>
                            <p className="text-gray-900 mt-1">{practice.phone}</p>
                          </div>
                        </div>
                      )}
                      {practice.address && (
                        <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                          <MapPin className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Address</p>
                            <p className="text-gray-900 mt-1">
                              {practice.address.street && `${practice.address.street}, `}
                              {practice.address.city && `${practice.address.city}, `}
                              {practice.address.state && `${practice.address.state} `}
                              {practice.address.zipCode && practice.address.zipCode}
                            </p>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No contact information available</p>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Practice Name and Description - Editable */}
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3 block">
              Practice Information
            </label>
            {isEditing ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Practice Name *
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Wellness Therapy Group"
                    minLength={3}
                    disabled={isSubmitting}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Brief description of your practice"
                    rows={3}
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed mt-1"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Name</p>
                  <p className="text-gray-900">{practice.name}</p>
                </div>
                {practice.description && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Description</p>
                    <p className="text-gray-900">{practice.description}</p>
                  </div>
                )}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Practice Type</p>
                  <p className="text-sm font-medium text-gray-900">
                    {practice.practiceType === 'group' ? 'Group Practice' : 'Solo Practice'}
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Created</p>
                  <p className="text-gray-900">
                    {new Date(practice.createdAt).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200">
          {isEditing ? (
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={handleCancel}
                disabled={isSubmitting}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleSave}
                disabled={isSubmitting || !formData.name.trim()}
                className="flex-1"
              >
                {isSubmitting ? (
                  <>
                    <Spinner size="small" />
                    <span className="ml-2">Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          ) : (
            <Button
              variant="primary"
              onClick={onClose}
              className="w-full"
            >
              Close
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default PracticeInfoModal;

